import React, {
  useEffect,
  useState,
  createContext,
  useContext,
  useMemo,
} from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useLocation } from 'react-router'
import moment from 'moment'
import { useMediaQuery, useMediaQueryScreen } from '~/hooks/useMediaQuery'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

const MessagingContext = createContext()

export const useMessaging = () => useContext(MessagingContext)

const queryClient = new QueryClient()

export const MessagingProvider = ({ children, ...props }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <MessagingProviderInner {...props}>{children}</MessagingProviderInner>
    </QueryClientProvider>
  )
}

export const formatTimeDiff = (dateTime, locale) => {
  const now = moment()
  const then = moment(dateTime)
  then.locale(locale || 'en')

  // Locale-aware connector for 'at'
  const atConnector = (function () {
    switch (locale || 'en') {
      case 'es':
      case 'es_es':
      case 'es_mx':
      case 'es_us':
        return 'a las'
      case 'fr':
      case 'fr_fr':
        return 'à'
      default:
        return 'at'
    }
  })()
  if (now.diff(then, 'days') < 1) {
    return then.format('h:mm A')
  } else if (now.diff(then, 'days') < 7) {
    return then.format(`dddd [${atConnector}] h:mm A`)
  } else if (now.diff(then, 'years') < 1) {
    return then.format(`MMMM D [${atConnector}] h:mm A`)
  } else {
    return then.format('MMM D, YYYY')
  }
}

const MessagingProviderInner = ({
  current_user_id,
  coach,
  coach_candidates,
  latest_thread_id,
  recipient_candidate,
  program_tier, // bronze, silver, gold, or null for coach
  locale,
  resume_review,
  resume_review_completed,
  candidates_coach_is_liaison,
  platform_name,
  children,
}) => {
  const [recipientCandidate, setRecipientCandidate] =
    useState(recipient_candidate)
  const currentUserId = parseInt(current_user_id)
  const userIsCoach = useMemo(
    () => currentUserId === parseInt(coach?.id),
    [currentUserId, coach?.id],
  )
  const [newMessageMode, setNewMessageMode] = useState(
    (userIsCoach && !!recipientCandidate) || resume_review,
  )
  const [currentThreadId, setCurrentThreadId] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) return null
    return latest_thread_id
  })
  const [viewingSentMessages, setViewingSentMessages] = useState(false)
  const coachInititatingMessage = userIsCoach && recipientCandidate
  const [newMessageType, setNewMessageType] = useState(
    coachInititatingMessage || resume_review ? 'coach' : '',
  )

  const location = useLocation()
  const isWideEnough = useMediaQueryScreen('lg')
  const isTallEnough = useMediaQuery('(min-height: 500px)')
  const isDesktop = isWideEnough && isTallEnough
  const isMobile = !isDesktop
  const [sentMessageId, setSentMessageId] = useState(null)

  useEffect(() => {
    if (!isDesktop && !location.hash.match(/^#\d+/)) {
      setCurrentThreadId(null)
    }
  }, [isDesktop])

  const programIsBronze = program_tier?.toLowerCase() === 'bronze'
  const programIsSilver = program_tier?.toLowerCase() === 'silver'
  const programIsGold = program_tier?.toLowerCase() === 'gold'
  const coachName = coach && [coach.first_name, coach.last_name].join(' ')
  const candidatesCoachIsLiaison = candidates_coach_is_liaison

  const clearNewMessageMode = () => {
    setNewMessageMode(false)
    setNewMessageType(null)
  }

  const goBackToList = () => {
    setCurrentThreadId(null)
    setNewMessageMode(false)
    setNewMessageType(null)
    setSentMessageId(null)
    setViewingSentMessages(false)
    window.location.hash = ''
  }

  const schema = z.object({
    search: z.string().optional(),
    category: z.string().optional(),
  })

  const form = useForm({
    defaultValues: {
      candidateSearch: '',
    },
    resolver: zodResolver(schema),
  })

  const { watch } = form

  const candidateSearch = watch('candidateSearch')

  useEffect(() => {
    if (location.hash === '#inbox') {
      // #inbox is a signal that we explitly want no current thread
      setCurrentThreadId(null)
      queryClient.invalidateQueries(['messageThreads'])
      return
    }
    const { threadId, messageId } =
      location.hash.match(/^#(?<threadId>\d+)-?(?<messageId>\d+)?$/)?.groups ||
      {}

    if (messageId && parseInt(messageId) > 0) {
      setSentMessageId(parseInt(messageId))
      setCurrentThreadId(parseInt(threadId) || 0)
      setViewingSentMessages(true)
    } else if (threadId && parseInt(threadId) > 0) {
      setSentMessageId(null)
      setCurrentThreadId(parseInt(threadId))
      setViewingSentMessages(false)
    } else {
      setCurrentThreadId(null)
      setSentMessageId(null)
    }
  }, [location.hash, isMobile, userIsCoach])

  const value = {
    currentUserId,
    currentThreadId,
    location,
    userIsCoach,
    coach,
    coachName,
    locale,
    recipientCandidate,
    setRecipientCandidate,
    newMessageMode,
    setNewMessageMode,
    clearNewMessageMode,
    newMessageType,
    setNewMessageType,
    queryClient,
    isMobile,
    isDesktop,
    goBackToList,
    programIsBronze,
    programIsSilver,
    programIsGold,
    viewingSentMessages,
    setViewingSentMessages,
    sentMessageId,
    setSentMessageId,
    resume_review,
    resume_review_completed,
    form,
    candidateSearch,
    coach_candidates,
    candidatesCoachIsLiaison,
    platform_name,
  }

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  )
}
